import { ClientCreation, ClientCreationWithErrors, CodePost, CP } from '@Arti-zen/package-backoffice'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, Dialog, Switch } from '@mui/material'
import InputTextControl from 'components/forms/InputText.control'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { Theme } from 'theme/theme'
import clientSchema from './client.schema'
import { useEffect, useState } from 'react'
import SelectMuiControl from 'components/forms/SelectMui.control'
import AutocompleteAsyncMuiHookForm from 'components/forms/AutocompleAsync.mui.hookform'
import { ApiArtizenBO } from 'utils/artizenConnector'
import { AutoFixHigh } from '@mui/icons-material'

type TProps = {
  client: ClientCreation | ClientCreationWithErrors
  onClose: () => void
  correctClient: (corrections: Partial<ClientCreation>) => void
}

export default function FormClientError({ client, onClose, correctClient }: TProps) {
  const [isLoadingCleanAddress, setIsLoadingCleanAddress] = useState<boolean>(false)
  const [isLoadingCleanClient, setIsLoadingCleanClient] = useState<boolean>(false)

  const getDefaultValues = (client?: ClientCreation) => ({
    lastName: client?.lastName ?? null,
    firstName: client?.firstName ?? null,
    phone: client?.phone ?? null,
    mobile: client?.mobile ?? null,
    email: client?.email ?? null,
    address: client?.address ?? null,
    additionalAddress: client?.additionalAddress ?? null,
    postalCode: client?.postalCode ?? null,
    city: client?.city ?? null,
    siren: client?.siren ?? null,
    clientNotes: client?.clientNotes ?? null,
    title: client?.title ?? null,
    isProfessional: Boolean(client?.isProfessional),
  })

  const optionsTitle = [
    { value: client?.title, label: <p style={{ pointerEvents: 'none', color: 'red', margin: 0 }}>{client?.title}</p> },
    ...['Mme', 'M.', 'M. et Mme'].map((civilite) => {
      return { value: civilite, label: civilite }
    }),
    { value: null, label: 'Aucun' },
  ]

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    watch,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: getDefaultValues(client), resolver: yupResolver(clientSchema), mode: 'onChange' })

  useEffect(() => {
    if (!client) return
    reset(getDefaultValues(client))
    trigger()
  }, [client])

  const isProfessional = watch('isProfessional')
  const siren = watch('siren')
  const title = watch('title')

  const submit = (data) => {
    correctClient(data)
    onClose()
  }

  const onError = (err: any) => {
    console.error('err : ', err)
  }

  const onCleanAddress = async () => {
    setIsLoadingCleanAddress(true)
    const result = await ApiArtizenBO.clients.cleanAdressAI(client)
    console.log({ result })
    if (result.client) {
      reset(getDefaultValues(result.client))
      await trigger()
    }
    setIsLoadingCleanAddress(false)
  }

  const onCleanClient = async () => {
    setIsLoadingCleanClient(true)
    const result = await ApiArtizenBO.clients.cleanClientAI(client)
    console.log({ result })
    if (result.client) {
      reset(getDefaultValues(result.client))
      await trigger()
    }
    setIsLoadingCleanClient(false)
  }

  return (
    <Dialog
      open={!!client}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '50%',
        },
      }}
    >
      <Container>
        <AIButton> {isLoadingCleanClient ? <CircularProgress /> : <AutoFixHigh onClick={onCleanClient} />}</AIButton>
        <FormContainer>
          <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Switch
              checked={isProfessional}
              onChange={() => {
                setValue('isProfessional', !isProfessional)
                trigger(['isProfessional', 'title', 'siren'])
              }}
            />
            <p>Client professionnel</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
            {(isProfessional === false || title) && (
              <SelectMuiControl
                items={optionsTitle}
                control={control}
                name={'title'}
                label={'Civilité'}
                error={errors?.['title']}
                style={{ flex: 0.5 }}
              />
            )}
            <InputTextControl
              control={control}
              name={'lastName'}
              label={'Nom*'}
              error={errors?.['lastName']}
              style={{ flex: 1 }}
            />
          </div>
          <InputTextControl control={control} name={'firstName'} label={'Prénom'} error={errors?.['firstName']} />
          <InputTextControl control={control} name={'mobile'} label={'Téléphone mobile'} error={errors?.['mobile']} />
          <InputTextControl control={control} name={'phone'} label={'Téléphone fixe'} error={errors?.['phone']} />
          <div style={{ gridColumn: 'span 2' }}>
            <InputTextControl control={control} name={'email'} label={'Email'} error={errors?.['email']} />
          </div>
          <div style={{ gridColumn: 'span 2', display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <InputTextControl control={control} name={'address'} label={'Adresse'} error={errors?.['address']} />
            </div>
            <AIButton>
              {isLoadingCleanAddress ? <CircularProgress /> : <AutoFixHigh onClick={onCleanAddress} />}
            </AIButton>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <InputTextControl
              control={control}
              name={'additionalAddress'}
              label={"Complément d'adresse"}
              error={errors?.['additionalAddress']}
            />
          </div>
          <div>
            <AutocompleteAsyncMuiHookForm
              fieldName="postalCode"
              label="Code postal"
              control={control}
              asyncSearch={async (text) =>
                CodePost.getByCP(text)
                  .slice(0, 35)
                  .map((code: CP, index) => {
                    return { id: index, label: `${code.cp}\t${code.ville}`, value: code, displayValue: code.cp }
                  })
              }
              onSelect={(optionSelected) => {
                setValue('postalCode', optionSelected?.value?.cp)
                setValue('city', optionSelected?.value?.ville)
              }}
              disableAutocomplete
              onClear={() => {
                setValue('city', '')
                setValue('postalCode', '')
              }}
            />
          </div>
          <AutocompleteAsyncMuiHookForm
            fieldName="city"
            label="Ville"
            control={control}
            asyncSearch={async (text) =>
              CodePost.getByVille(text)
                .slice(0, 35)
                .map((code: CP, index) => {
                  return { id: index, label: `${code.cp}\t${code.ville}`, value: code, displayValue: code.ville }
                })
            }
            onSelect={(optionSelected) => {
              setValue('postalCode', optionSelected?.value?.cp)
              setValue('city', optionSelected?.value?.ville)
            }}
            onClear={() => {
              setValue('city', '')
              setValue('postalCode', '')
            }}
            disableAutocomplete
          />
          {(isProfessional || siren) && (
            <div style={{ gridColumn: 'span 2' }}>
              <InputTextControl control={control} name={'siren'} label={'N° de SIRET'} error={errors?.['siren']} />
            </div>
          )}
        </FormContainer>
        <Button
          onClick={handleSubmit(submit, onError)}
          style={{
            display: 'flex',
            alignSelf: 'center',
            backgroundColor: Theme.colors.blueArtizen,
            color: 'white',
            width: '100%',
            marginTop: 40,
          }}
        >
          Valider
        </Button>
      </Container>
    </Dialog>
  )
}

const Container = styled.div`
  padding: 30px 20px;
`

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  align-items: start;
`
const AIButton = styled.div`
  background-color: rebeccapurple;
  height: 56px;
  width: 56px;
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  &:hover {
    opacity: 0.6;
    cursor: pointer;
  }
`
